import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MicrositesManager = () => {
  const [microsites, setMicrosites] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    client_name: '',
    slug: '',
    logo_url: '',
    description: ''
  });

  const fetchMicrosites = async () => {
    try {
      const response = await axios.get(`${API}/microsites`);
      setMicrosites(response.data);
    } catch (error) {
      toast.error('Failed to fetch microsites');
    }
  };

  useEffect(() => {
    fetchMicrosites();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    try {
      await axios.post(`${API}/microsites`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Microsite created successfully');
      setIsDialogOpen(false);
      setFormData({
        client_name: '',
        slug: '',
        logo_url: '',
        description: ''
      });
      fetchMicrosites();
    } catch (error) {
      toast.error('Failed to create microsite');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this microsite?')) return;
    
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/microsites/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Microsite deleted');
      fetchMicrosites();
    } catch (error) {
      toast.error('Failed to delete microsite');
    }
  };

  return (
    <div data-testid="microsites-manager">
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-3xl font-heading font-bold text-maroon">Client Microsites</h2>
        <Button onClick={() => setIsDialogOpen(true)} className="btn-primary" data-testid="add-microsite-btn">
          <Plus size={20} className="mr-2" />
          Add Microsite
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {microsites.map((ms) => (
          <div key={ms.id} className="card" data-testid="microsite-item">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-bold text-xl text-maroon">{ms.client_name}</h3>
              <Button 
                onClick={() => handleDelete(ms.id)} 
                variant="destructive" 
                size="sm"
                data-testid="delete-microsite-btn"
              >
                <Trash2 size={16} />
              </Button>
            </div>
            <p className="text-maroon/70 text-sm mb-3">{ms.description}</p>
            <div className="bg-sand/30 p-3 rounded-sm border border-maroon/10">
              <p className="text-xs text-maroon/60 mb-1">Access URL:</p>
              <p className="font-mono text-sm text-maroon">/clients/{ms.slug}</p>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-maroon">Add Client Microsite</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="microsite-form">
            <div className="space-y-2">
              <Label htmlFor="client_name">Client Name *</Label>
              <Input
                id="client_name"
                value={formData.client_name}
                onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                required
                className="input-field"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="slug">URL Slug *</Label>
              <Input
                id="slug"
                placeholder="e.g., acme-corp"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                required
                className="input-field"
              />
              <p className="text-xs text-maroon/60">Will be accessible at: /clients/{formData.slug || 'your-slug'}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="logo_url">Logo URL (optional)</Label>
              <Input
                id="logo_url"
                type="url"
                value={formData.logo_url}
                onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                className="input-field"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Welcome message for client members..."
                required
                rows={4}
                className="bg-white border-2 border-maroon/20 focus:border-maroon focus:ring-0 rounded-sm resize-none"
              />
            </div>

            <Button type="submit" className="btn-primary w-full">
              Create Microsite
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MicrositesManager;